'use client';

import { useEffect, useState } from 'react';
import { Tabs, Tab } from '@heroui/tabs';
import dayjs from 'dayjs';
import Link from 'next/link';
import Image from 'next/image';
import Exception from '@/components/Exception';
import { CarVideoItem, CarPosterItem, CarArticleItem, CarQuestionsItem } from '@/app/car/[...param]/api';
import * as api from '../api';
import { h5_path } from '@/constants';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
interface Props {
  shopId: number;
  seriesId: number;
  textImgList?: string[];
}
interface MktOthers {
  carVideoData?: CarVideoItem[];
  carPosterData?: CarPosterItem[];
  carNewsData?: CarArticleItem[];
  carQuestionsData?: CarQuestionsItem[];
}
export default function Index({ seriesId, shopId, textImgList }: Props) {
  const [selectedKey, setSelectedKey] = useState<React.Key>('图文详情');
  const [mktOthers, setMktOthers] = useState<MktOthers>({});
  useEffect(() => {
    if (selectedKey !== '图文详情') {
      getList();
    }
  }, [selectedKey]);

  async function getList() {
    if (selectedKey === '视频' && !mktOthers.carVideoData) {
      const carVideoData = await api.getCarVideoApi({ seriesId, shopId });
      setMktOthers({ ...mktOthers, carVideoData });
    }
    if (selectedKey === '海报' && !mktOthers.carPosterData) {
      const carPosterData = await api.getCarPosterApi({ seriesId, shopId });
      setMktOthers({ ...mktOthers, carPosterData });
    }
    if (selectedKey === '文章' && !mktOthers.carNewsData) {
      const carNewsData = await api.getCarNewsApi({ seriesId, shopId });
      setMktOthers({ ...mktOthers, carNewsData });
    }
    if (selectedKey === '问答' && !mktOthers.carQuestionsData) {
      const carQuestionsData = await api.getCarQuestionsApi({ seriesId, shopId });
      setMktOthers({ ...mktOthers, carQuestionsData });
    }
  }
  return (
    <div className="flex w-full flex-col bg-white dark:bg-gray-900 mt-[10px]">
      <Tabs
        aria-label="Options"
        onSelectionChange={v => setSelectedKey(v)}
        classNames={{
          tabList: 'flex justify-between w-full relative rounded-none pl-[15px] pr-[15px] border-divider',
          cursor: 'w-full bg-[#ff921c]',
          tab: 'max-w-fit px-0 h-12',
          tabContent: 'group-data-[selected=true]:text-[#ff921c]',
        }}
        className="sticky top-0 bg-white dark:bg-[#171717]"
        color="primary"
        variant="underlined"
      >
        <Tab
          key="图文详情"
          title={
            <div className="flex items-center space-x-2">
              <span className="dark:text-white">图文详情</span>
            </div>
          }
        >
          {textImgList?.map((v, i) => <Image key={i} sizes="100vw" width={375} height={100} alt="" style={{ width: '100%', height: 'auto' }} quality={100} src={v} />)}
        </Tab>
        <Tab
          key="视频"
          title={
            <div className="flex items-center space-x-2">
              <span className="dark:text-white">视频</span>
            </div>
          }
        >
          <div>
            {mktOthers.carVideoData ? (
              mktOthers.carVideoData?.map((item, index) => (
                <video key={index} controls preload="metadata" loop={false} muted={false} poster="" style={{ width: '100%', height: 210, marginBottom: 15 }}>
                  <source src={item.videoUrl} type="video/mp4" />
                  您的浏览器不支持视频播放。
                </video>
              ))
            ) : (
              <div className="empty">
                <Exception msg="暂无数据" />
              </div>
            )}
          </div>
        </Tab>
        <Tab
          key="海报"
          title={
            <div className="flex items-center space-x-2">
              <span className="dark:text-white">海报</span>
            </div>
          }
        >
          <div>
            {mktOthers.carPosterData ? (
              <div className="grid grid-cols-2 gap-2 max-w-md m-auto  md:grid-cols-4 md:gap-4 md:max-w-full">
                {mktOthers.carPosterData?.map(item => (
                  <div key={item.id} style={{ height: 225, width: 168 }} className="bg-white rounded-lg overflow-hidden shadow-md m-auto">
                    <PhotoProvider bannerVisible={false} photoClosable>
                      <PhotoView src={item.previewUrl}>
                        <Image src={item.previewUrl} alt="car" width={168} height={225} className="h-full w-full rounded-xs" />
                      </PhotoView>
                    </PhotoProvider>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty">
                <Exception msg="暂无数据" />
              </div>
            )}
          </div>
        </Tab>
        <Tab
          key="文章"
          title={
            <div className="flex items-center space-x-2">
              <span className="dark:text-white">文章</span>
            </div>
          }
        >
          <div>
            {mktOthers.carNewsData ? (
              mktOthers.carNewsData.map(item => (
                <Link key={item.id} href={`${h5_path}/articlelib.html?id=${item.id}&shopId=${shopId}`} className="flex flex-row items-center p-2 border-t border-gray-100 dark:border-[#0a0a0a]">
                  <Image src={item.imageUrl} alt="car" width={50} height={50} className="rounded-xs" />
                  <div className="ml-[12px]">
                    <div className="text-[#333333] dark:text-white">{item.title}</div>
                    <div className="text-[#999999] text-xs">{dayjs(item.createTime).format('YYYY-MM-DD')}</div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="empty">
                <Exception msg="暂无数据" />
              </div>
            )}
          </div>
        </Tab>
        <Tab
          key="问答"
          title={
            <div className="flex items-center space-x-2">
              <span className="dark:text-white">问答</span>
            </div>
          }
        >
          <div className="pl-[5px] pr-[5px]">
            {mktOthers.carQuestionsData ? (
              mktOthers.carQuestionsData?.map(item => {
                return (
                  <div key={`ques_${item.question}`} className="pt-[10px] pb-[10px] border-t border-gray-100 dark:border-[#0a0a0a]">
                    <div className="flex items-center">
                      <div className="p-0.5 bg-[#FF921C] text-white mr-2 text-[12px] rounded-[2px] leading-[12px]">问</div>
                      <div className="font-medium">
                        {item.question}
                        {item.question.endsWith('？') ? '' : '？'}
                      </div>
                    </div>
                    <div className="flex items-baseline mt-[10px]">
                      <div className="p-0.5 bg-[#20C688] text-white mr-2 text-[12px] rounded-[2px] leading-[12px]">答</div>
                      <div
                        className="text-[#666666] text-xs"
                        ref={node => {
                          // eslint-disable-next-line no-param-reassign
                          node && (node.innerHTML = item.answer || '暂无内容');
                        }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty">
                <Exception msg="暂无数据" />
              </div>
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}
